import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart3, TrendingUp, Target, Award, Star, CheckCircle, Loader2, Trophy, ArrowUp, 
  Lightbulb, AlertTriangle, TrendingDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Performance = () => {
  const { pro, proId } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/stats/${proId}`);
        setStats(res.data);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [proId]);

  // Process service performance data - useMemo must be called before any early returns
  const servicePerformanceData = React.useMemo(() => {
    if (!stats?.service_performance) return [];
    
    const services = Object.entries(stats.service_performance).map(([serviceId, data]) => {
      const leadsUnlocked = data.leads_unlocked || 0;
      const jobsWon = data.jobs_won || 0;
      const conversionRate = leadsUnlocked > 0 ? (jobsWon / leadsUnlocked) * 100 : 0;
      
      return {
        serviceId,
        serviceName: serviceId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), // Format service name
        leadsUnlocked,
        jobsWon,
        conversionRate: conversionRate.toFixed(1)
      };
    });
    
    // Sort by conversion rate (best performing first)
    return services.sort((a, b) => parseFloat(b.conversionRate) - parseFloat(a.conversionRate));
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
        <p className="text-sm text-gray-500 mt-1">Track your activity and success metrics.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Leads Unlocked */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Target size={18} className="text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Leads Unlocked</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.total_leads_unlocked || 0}</p>
          <p className="text-xs text-gray-500 mt-1">total opportunities</p>
        </div>

        {/* Conversion Rate */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <TrendingUp size={18} className="text-green-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Conversion Rate</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.conversion_rate || 0}%</p>
          <p className="text-xs text-gray-500 mt-1">leads to jobs</p>
        </div>

        {/* Jobs Completed */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <CheckCircle size={18} className="text-purple-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Jobs Completed</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.jobs_completed || 0}</p>
          <p className="text-xs text-gray-500 mt-1">successful projects</p>
        </div>

        {/* Rating */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Star size={18} className="text-amber-600" />
            </div>
            <span className="text-sm font-medium text-gray-700">Rating</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900">{stats?.rating || 0}</p>
            <Star size={18} className="text-amber-400 fill-amber-400" />
          </div>
          <p className="text-xs text-gray-500 mt-1">{stats?.review_count || 0} reviews</p>
        </div>
      </div>

      {/* Smart Recommendations - Top 2-3 Insights */}
      {stats?.recommendations && stats.recommendations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Lightbulb size={20} className="text-gray-600" />
            Key Insights
          </h2>
          <div className="space-y-3">
            {stats.recommendations.slice(0, 3).map((rec, index) => {
              const isWarning = rec.type === 'warning';
              const isOpportunity = rec.type === 'opportunity';
              const isFocus = rec.type === 'focus';
              
              return (
                <div
                  key={index}
                  data-testid={`recommendation-${rec.type}-${index}`}
                  className={`rounded-lg p-4 border-l-4 ${
                    isWarning
                      ? 'bg-yellow-50 border-yellow-400'
                      : isOpportunity
                      ? 'bg-blue-50 border-blue-400'
                      : 'bg-green-50 border-green-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isWarning
                        ? 'bg-yellow-100'
                        : isOpportunity
                        ? 'bg-blue-100'
                        : 'bg-green-100'
                    }`}>
                      {isWarning ? (
                        <AlertTriangle size={16} className="text-yellow-600" />
                      ) : isOpportunity ? (
                        <TrendingUp size={16} className="text-blue-600" />
                      ) : (
                        <Trophy size={16} className="text-green-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 leading-relaxed">{rec.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-bold ${
                          isWarning
                            ? 'text-yellow-700'
                            : isOpportunity
                            ? 'text-blue-700'
                            : 'text-green-700'
                        }`}>
                          {rec.metric}
                        </span>
                        <span className="text-xs text-gray-500">• {rec.service_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Credits Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 size={20} className="text-gray-600" />
          Credits Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Total Purchased</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.total_credits_purchased || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Total Spent</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.total_credits_spent || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Current Balance</p>
            <p className="text-2xl font-bold text-[#37b34a]">{stats?.credits_balance || 0}</p>
          </div>
        </div>
      </div>

      {/* Service Performance Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="service-performance-section">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Trophy size={20} className="text-gray-600" />
          Performance by Service
        </h2>
        
        {servicePerformanceData.length === 0 ? (
          <div className="text-center py-12" data-testid="service-performance-empty">
            <Trophy size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No service performance data yet.</p>
            <p className="text-xs text-gray-400 mt-1">Unlock leads and mark jobs as won to see your performance metrics.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="service-performance-table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Service</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Leads Unlocked</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Jobs Won</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">
                    <div className="flex items-center justify-center gap-1">
                      Conversion Rate
                      <ArrowUp size={14} className="text-green-600" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {servicePerformanceData.map((service, index) => (
                  <tr 
                    key={service.serviceId} 
                    data-testid={`service-row-${service.serviceId}`}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      index === 0 ? 'bg-green-50/30' : ''
                    }`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        {index === 0 && (
                          <Trophy size={16} className="text-[#37b34a]" data-testid="top-performer-trophy" />
                        )}
                        <span className="text-sm font-medium text-gray-900" data-testid={`service-name-${service.serviceId}`}>{service.serviceName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-sm text-gray-700" data-testid={`leads-unlocked-${service.serviceId}`}>{service.leadsUnlocked}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-sm font-semibold text-gray-900" data-testid={`jobs-won-${service.serviceId}`}>{service.jobsWon}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span 
                        data-testid={`conversion-rate-${service.serviceId}`}
                        className={`text-sm font-bold ${
                          parseFloat(service.conversionRate) >= 50 
                            ? 'text-green-600' 
                            : parseFloat(service.conversionRate) >= 25 
                              ? 'text-amber-600' 
                              : 'text-gray-600'
                        }`}
                      >
                        {service.conversionRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-xs text-gray-500 flex items-start gap-1.5">
              <Trophy size={14} className="text-[#37b34a] mt-0.5 flex-shrink-0" />
              <span>Top performing service highlighted. Conversion rate = (Jobs Won / Leads Unlocked) × 100%</span>
            </div>
          </div>
        )}
      </div>

      {/* Profile Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Award size={20} className="text-gray-600" />
          Profile Status
        </h2>
        <div className="space-y-4">
          {/* Profile Completeness */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Profile Completeness</p>
              <p className="text-sm font-bold text-[#37b34a]">{stats?.profile_completeness || 40}%</p>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div
                className="bg-[#37b34a] h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${stats?.profile_completeness || 40}%` }}
              />
            </div>
            {(stats?.profile_completeness || 40) < 100 && (
              <p className="text-xs text-gray-500 mt-2">
                Complete your profile to attract more customers and unlock additional features.
              </p>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {stats?.is_verified && (
              <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <CheckCircle size={12} /> Verified
              </span>
            )}
            <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full capitalize">
              {stats?.subscription_status || 'free'} Plan
            </span>
            {(stats?.response_rate || 0) >= 80 && (
              <span className="bg-green-50 text-green-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <TrendingUp size={12} /> High Response Rate
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Performance;
